/**
 * Fetch Interceptor for WebZjs WASM
 * 
 * The WebZjs WASM makes fetch() calls to lightwalletd but doesn't include proper gRPC-web headers.
 * This interceptor adds the required headers to make requests work with our nginx/lightwalletd setup.
 * 
 * It also caches wallet scanner notes and provides them when WASM tries to query unified addresses.
 */

let interceptorInstalled = false;

// Storage for wallet scanner notes to provide to WASM when it queries
interface NoteCache {
	[address: string]: any[]; // OrchardWitnessNote[]
}
const noteCache: NoteCache = {};

export const cacheNotesForAddress = (unifiedAddress: string, notes: any[], transparentReceiver?: string) => {
	noteCache[unifiedAddress] = notes;
	console.log(`📝 Cached ${notes.length} notes for ${unifiedAddress.substring(0, 20)}...`);
	
	// Also cache for the transparent component if provided
	if (transparentReceiver) {
		noteCache[transparentReceiver] = notes;
		console.log(`📝 Also cached notes for transparent receiver: ${transparentReceiver}`);
	}
};

export const clearNoteCacheForAddress = (unifiedAddress: string, transparentReceiver?: string) => {
	delete noteCache[unifiedAddress];
	console.log(`🗑️ Cleared note cache for ${unifiedAddress.substring(0, 20)}...`);
	
	if (transparentReceiver) {
		delete noteCache[transparentReceiver];
		console.log(`🗑️ Also cleared transparent receiver cache: ${transparentReceiver}`);
	}
};

export function installFetchInterceptor() {
	if (interceptorInstalled) {
		console.log('✅ Fetch interceptor already installed');
		return;
	}

	// IMPORTANT: Save the CURRENT window.fetch (which may already be wrapped by fetch-retry)
	// We need to be the OUTERMOST wrapper so we intercept ALL requests
	const originalFetch = window.fetch.bind(window);
	const windowAny = window as any;
	if (!windowAny.__WB32_NATIVE_FETCH__) {
		windowAny.__WB32_NATIVE_FETCH__ = originalFetch;
	}

	window.fetch = async function(...args: Parameters<typeof fetch>): Promise<Response> {
		let [url, init] = args;

		// Convert URL to string if it's a Request object
		const urlString = url instanceof Request ? url.url : url.toString();

		// If WASM is calling lightwalletd, we need to handle it intelligently:
		// - gRPC-web requests (compact blocks, etc.) → lightwalletd with proper headers
		// - JSON-RPC requests (chain info) → redirect to Zebra RPC
		if (urlString.includes('lightwalletd') && !urlString.includes('rpc.zcash')) {
			let shouldRedirectToZebra = false;
			
			// Check if this is a JSON-RPC request that should go to Zebra instead
			if (init?.method === 'POST' && init?.body) {
				try {
					const bodyStr = typeof init.body === 'string' ? init.body : '';
					const bodyJson = JSON.parse(bodyStr);
					
					// JSON-RPC methods that need Zebra (not lightwalletd)
					const zebraRpcMethods = ['getblockchaininfo', 'getblockhash', 'getblock', 'getbestblockhash', 
						'getblockcount', 'getaddressbalance', 'getaddressutxos', 'z_validateaddress'];
					
					if (bodyJson.jsonrpc && zebraRpcMethods.includes(bodyJson.method)) {
						console.log(`🔀 Redirecting JSON-RPC ${bodyJson.method} from lightwalletd to Zebra`);
						// Change the URL to Zebra RPC
						url = 'https://rpc.zcash.winbit32.com';
						shouldRedirectToZebra = true;
					}
				} catch (e) {
					// Not JSON, must be gRPC-web - continue to add headers below
				}
			}
			
			// If not redirecting to Zebra, check what kind of request this is
			if (!shouldRedirectToZebra) {
				// Try to detect if this is actually a JSON-RPC request we missed
				let isJsonRpc = false;
				if (init?.body) {
					try {
						const bodyStr = typeof init.body === 'string' ? init.body : '';
						const bodyJson = JSON.parse(bodyStr);
						if (bodyJson.jsonrpc) {
							isJsonRpc = true;
							console.log(`⚠️ WARNING: JSON-RPC request to lightwalletd detected: ${bodyJson.method}`);
							console.log(`   This will fail - lightwalletd only supports gRPC-web!`);
							console.log(`   Redirecting to Zebra RPC...`);
							url = 'https://rpc.zcash.winbit32.com';
							shouldRedirectToZebra = true;
						}
					} catch (e) {
						// Not JSON, definitely gRPC-web
					}
				}
				
				if (!isJsonRpc) {
					console.log(`🔧 Adding gRPC-web headers for lightwalletd request`);
					init = init || {};
					const headers = new Headers(init.headers || {});
					
					if (!headers.has('Content-Type')) {
						headers.set('Content-Type', 'application/grpc-web+proto');
					}
					if (!headers.has('Accept')) {
						headers.set('Accept', 'application/grpc-web+proto');
					}
					
					init.headers = headers;
					console.log(`✅ Added gRPC-web headers`);
					
					return originalFetch(url, init);
				}
			}
			// If redirecting to Zebra, fall through to Zebra RPC handling below
		}
		
		if (urlString.includes('rpc.zcash') || url.toString().includes('rpc.zcash')) {
			console.log(`📡 Zebra RPC request: ${urlString}`);
			
			// Check if this is a balance or UTXO query for a unified address we have cached notes for
			if (init?.method === 'POST' && init?.body) {
				try {
					const bodyStr = typeof init.body === 'string' ? init.body : '';
					const bodyJson = JSON.parse(bodyStr);
					
					// Check if this is a balance or UTXO query
					const method = bodyJson.method;
					const params = bodyJson.params;
					
					// Log the request for debugging
					console.log(`📋 RPC Method: ${method}`, params);
					
					if ((method === 'getaddressbalance' || method === 'getaddressutxos') && params && params[0]) {
						const addresses = params[0].addresses || [params[0]];
						// Check for both unified and transparent addresses in our cache
						const cachedAddr = addresses.find((addr: string) => noteCache[addr]);
						
						if (cachedAddr && noteCache[cachedAddr]) {
							console.log(`🎯 Intercepting ${method} for address ${cachedAddr.substring(0, 10)}... with ${noteCache[cachedAddr].length} cached notes`);
							
							// Return the cached note data formatted as RPC response
							const notes = noteCache[cachedAddr];
							const totalValue = notes.reduce((sum, note) => sum + (note.value || 0), 0);
							
							let result;
							if (method === 'getaddressbalance') {
								result = {
									balance: totalValue,
									received: totalValue
								};
							} else {
								// getaddressutxos - format notes as if they were UTXOs
								result = notes.map((note, index) => ({
									txid: note.commitment || '0'.repeat(64), // Use commitment as pseudo-txid
									outputIndex: index,
									script: '', // Not needed for Orchard
									satoshis: note.value || 0,
									height: note.block_height || 0
								}));
							}
							
							const mockResponse = {
								jsonrpc: '2.0',
								id: bodyJson.id || 1,
								result: result
							};
							
							console.log(`✅ Returning mocked RPC response with cached note data (${totalValue} zatoshis)`);
							return new Response(JSON.stringify(mockResponse), {
								status: 200,
								headers: { 'Content-Type': 'application/json' }
							});
						}
					}
				} catch (e) {
					// Not JSON or parsing failed, continue with original request
					console.log('📡 Standard RPC request, passing through');
				}
			}
		}

		return originalFetch(url, init);
	};

	windowAny.__WB32_FETCH_INTERCEPTOR__ = window.fetch;
	interceptorInstalled = true;
	console.log('✅ Fetch interceptor installed for WebZjs WASM gRPC-web compatibility');
}

export function uninstallFetchInterceptor() {
	// We can't truly uninstall without keeping a reference to original fetch
	// For now, just mark as not installed
	interceptorInstalled = false;
	console.log('⚠️ Fetch interceptor marked as uninstalled (requires page reload to fully remove)');
}

export function isInterceptorInstalled(): boolean {
	return interceptorInstalled;
}

// Install the interceptor IMMEDIATELY when this module loads (before any other fetch wrappers)
// This ensures we're the innermost wrapper and can intercept all requests
if (typeof window !== 'undefined' && !interceptorInstalled) {
	console.log('🚀 Auto-installing fetch interceptor at module load time...');
	installFetchInterceptor();
}
